/**
 * The first paint, before the record has arrived.
 *
 * Deliberately not a spinner. A spinner says "working" and nothing else; three settling bars in
 * the shape of the card that is about to appear say where the content is going to be, which is the
 * whole point of a skeleton. There is no bespoke keyframe here either — `ft-rise` is the one the
 * prototype already declares, and inventing a shimmer would mean adding CSS to portal.css, which
 * is a verbatim copy of the approved prototype's own block.
 *
 * `role="status"` carries the sentence to a screen reader; the bars are decoration and are hidden
 * from it, or the announcement becomes a list of blank regions.
 */

import type { ReactElement } from 'react'

import { BrandPaw } from '../model/icons'

export interface LoadingProps {
  /** Say what is being waited for. "Loading…" tells a worried client nothing. */
  readonly label?: string
}

/** One skeleton bar. Widths are staggered so the block reads as text, not as a table. */
function Bar({ width }: { readonly width: string }): ReactElement {
  return (
    <span
      style={{
        display: 'block',
        width,
        height: '13px',
        borderRadius: '999px',
        background: 'var(--color-neutral-200)',
      }}
    />
  )
}

export function Loading({ label = 'Getting your record ready…' }: LoadingProps): ReactElement {
  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '26px 18px calc(26px + env(safe-area-inset-bottom))',
      }}
    >
      <section
        className="card elev-sm"
        style={{ width: 'min(430px, 100%)', gap: 0, padding: '30px 26px', animation: 'ft-rise .4s ease both' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '22px' }}>
          <span
            aria-hidden="true"
            style={{
              width: '46px',
              height: '46px',
              flex: 'none',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            <BrandPaw />
          </span>
          <span>
            <span style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: '19px', lineHeight: 1.1 }}>
              Fairy Tails
            </span>
            <span className="text-muted" style={{ display: 'block', fontSize: '12px', marginTop: '2px' }}>
              My Dog · client portal
            </span>
          </span>
        </div>

        <p role="status" style={{ margin: '0 0 20px', fontSize: '14px' }}>
          {label}
        </p>

        <div aria-hidden="true" style={{ display: 'grid', gap: '11px' }}>
          <Bar width="72%" />
          <Bar width="100%" />
          <Bar width="54%" />
        </div>
      </section>
    </main>
  )
}
